import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../api.js';
import { AiStarCallout } from '../components/AiStarCallout.js';
import { AssistantComposer } from '../components/AssistantComposer.js';
import { MemoryPanel } from '../components/AssistantMemoryPanel.js';
import { AssistantTurns, EmptyState, Spark } from '../components/AssistantTurns.js';
import { PausedBanner } from '../components/ui.js';
import {
  type AssistantAnswer,
  type IngestBrief,
  type MemoryNote,
  type Watch,
  addMemory,
  confirmProductLine,
  createWatch,
  deleteWatch,
  listBriefs,
  listMemory,
  listWatches,
  removeMemory,
} from '../lib/assistant.js';
import {
  type SavedReport,
  deleteSavedReport,
  listSavedReports,
  saveReport,
  scheduleReport,
} from '../lib/reports.js';
import { useAssistantStream } from '../lib/useAssistantStream.js';

const SUGGESTIONS = [
  'How is my book doing this month?',
  'Which carrier is performing worst?',
  "What's my attrition trend?",
  'How do I set up a rate schedule?',
];

/**
 * Sample questions for the empty state, tailored to what the account can
 * actually ask about: gated-report samples only appear when the matching
 * feature (proof-out / payment gaps / medicare lifecycle) is enabled.
 */
function suggestionsFor(entitlements: Record<string, boolean>): string[] {
  const out = [...SUGGESTIONS];
  if (entitlements.chargebackProof) out.splice(1, 0, 'Were my chargebacks actually paid first?');
  if (entitlements.paymentGaps) out.splice(2, 0, 'Which months did a carrier never pay me?');
  if (entitlements.medicareLifecycle) out.splice(3, 0, "What's my renewal forecast?");
  return out.slice(0, 6);
}

export function Assistant() {
  const { token, baseUrl, aiPaused, entitlements } = useAuth();
  const {
    turns,
    input,
    setInput,
    busy,
    newChat,
    runSaved,
    send,
    onKeyDown,
    onInvestigate,
    attach,
    repreview,
    approveIngest,
    ingestPreviewed,
  } = useAssistantStream();
  const [saved, setSaved] = useState<SavedReport[]>([]);
  const [latestBrief, setLatestBrief] = useState<IngestBrief | null>(null);
  const [briefs, setBriefs] = useState<IngestBrief[]>([]);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [memory, setMemory] = useState<MemoryNote[]>([]);
  // The chat stays CLEAN — everything the assistant remembers (notes, watches,
  // saved reports, statement briefs) lives on its own tab. A bulk backfill once
  // buried the chat under its own activity; never again.
  const [tab, setTab] = useState<'chat' | 'memory'>('chat');
  const scrollerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-scroll whenever turns change
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  const loadSaved = () => {
    listSavedReports(baseUrl, token)
      .then((r) => setSaved(r.reports))
      .catch(() => {});
  };
  const loadWatches = () => {
    listWatches(baseUrl, token)
      .then((r) => setWatches(r.data))
      .catch(() => {});
  };
  const loadMemory = () => {
    listMemory(baseUrl, token)
      .then((r) => setMemory(r.data))
      .catch(() => {});
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load saved reports + latest brief + watches + memory once on mount
  useEffect(() => {
    loadSaved();
    loadWatches();
    loadMemory();
    listBriefs(baseUrl, token, 10)
      .then((r) => {
        setBriefs(r.data);
        // The chat banner is reserved for a FRESH brief (statement processed in
        // the last 48h) — older activity lives on the Memory & activity tab.
        const latest = r.data[0] ?? null;
        const fresh =
          latest && Date.now() - new Date(latest.createdAt).getTime() < 48 * 60 * 60 * 1000;
        setLatestBrief(fresh ? latest : null);
      })
      .catch(() => {});
  }, []);

  const onAddMemory = async (note: string) => {
    try {
      await addMemory(baseUrl, token, note);
      loadMemory();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not save the note.');
    }
  };
  const onRemoveMemory = async (n: MemoryNote) => {
    try {
      await removeMemory(baseUrl, token, n.id);
      loadMemory();
    } catch {
      // list reconciles on next load
    }
  };

  // Watch a recovery question: re-runs after every ingest, emails on an increase.
  const onWatch = async (question: string) => {
    try {
      await createWatch(baseUrl, token, question);
      loadWatches();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not create the watch.');
    }
  };

  const removeWatch = async (w: Watch) => {
    try {
      await deleteWatch(baseUrl, token, w.id);
      loadWatches();
    } catch {
      // list reconciles on next load
    }
  };

  // Save a book answer's query as a named, re-runnable report.
  const onSave = async (answer: AssistantAnswer) => {
    if (!answer.sql) return;
    const name = window.prompt('Name this report', answer.question?.slice(0, 60) ?? 'My report');
    if (!name?.trim()) return;
    try {
      await saveReport(baseUrl, token, {
        name: name.trim(),
        question: answer.question,
        sql: answer.sql,
      });
      loadSaved();
    } catch {
      // surfaced via the next list refresh; keep the chat uninterrupted
    }
  };

  const removeSaved = async (report: SavedReport) => {
    try {
      await deleteSavedReport(baseUrl, token, report.id);
      loadSaved();
    } catch {
      // ignore — the list will reconcile on next load
    }
  };

  const scheduleSaved = async (report: SavedReport, cadence: 'off' | 'weekly' | 'monthly') => {
    try {
      await scheduleReport(baseUrl, token, report.id, cadence);
      loadSaved();
    } catch {
      // ignore — the list will reconcile on next load
    }
  };

  return (
    <div className="flex h-[calc(100vh-10rem)] flex-col">
      <div className="mb-4 flex items-center gap-3">
        <Spark />
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Ask CommissionSight</h1>
          <p className="text-sm text-neutral-500">
            Ask about your book, ask how CommissionSight works, or have it set things up for you.
          </p>
        </div>
      </div>

      <AiStarCallout onAssistantPage />

      {aiPaused && (
        <div className="mb-4">
          <PausedBanner message="The AI assistant is paused for your account. Add a payment method on the Profile page or contact support to resume." />
        </div>
      )}

      <div className="mb-3 flex items-center gap-1">
        {(
          [
            { id: 'chat', label: 'Chat' },
            { id: 'memory', label: 'Memory & activity' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
              tab === t.id
                ? 'bg-emerald-600 text-white'
                : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
            }`}
          >
            {t.label}
            {t.id === 'memory' && memory.length + watches.length + saved.length > 0 && (
              <span
                className={`ml-1.5 rounded-full px-1.5 text-xs ${tab === t.id ? 'bg-emerald-700' : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800'}`}
              >
                {memory.length + watches.length + saved.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'memory' ? (
        <div className="flex-1 overflow-y-auto rounded-2xl border border-neutral-200 bg-neutral-50/60 p-5 dark:border-neutral-800 dark:bg-neutral-900/40">
          <MemoryPanel
            memory={memory}
            onAddMemory={onAddMemory}
            onRemoveMemory={onRemoveMemory}
            watches={watches}
            onDeleteWatch={removeWatch}
            saved={saved}
            onRun={(r) => {
              setTab('chat');
              runSaved(r);
            }}
            onDelete={removeSaved}
            onSchedule={scheduleSaved}
            briefs={briefs}
          />
        </div>
      ) : (
        <div
          ref={scrollerRef}
          className="flex-1 space-y-6 overflow-y-auto rounded-2xl border border-neutral-200 bg-neutral-50/60 p-5 dark:border-neutral-800 dark:bg-neutral-900/40"
        >
          {turns.length === 0 ? (
            <>
              {latestBrief && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                  <p className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    Latest statement brief · {latestBrief.carrierName ?? 'carrier'} ·{' '}
                    {latestBrief.period}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">
                    {latestBrief.brief}
                  </p>
                </div>
              )}
              <EmptyState onPick={send} suggestions={suggestionsFor(entitlements)} />
            </>
          ) : (
            <>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={newChat}
                  disabled={busy}
                  className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-500 transition hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-40 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400"
                  title="Start a fresh conversation (this one stays saved)"
                >
                  + New chat
                </button>
              </div>
              <AssistantTurns
                turns={turns}
                busy={busy}
                onAsk={send}
                onSave={onSave}
                onWatch={onWatch}
                onInvestigate={onInvestigate}
                onIngest={ingestPreviewed}
                onRepreview={repreview}
                onConfirmProductLine={(carrierId, choice) =>
                  confirmProductLine(baseUrl, token, carrierId, choice)
                }
                onApproveIngest={approveIngest}
              />
            </>
          )}
        </div>
      )}

      <div className={`mt-4 ${tab === 'memory' ? 'hidden' : ''}`}>
        <AssistantComposer
          input={input}
          onInputChange={setInput}
          onKeyDown={onKeyDown}
          onSend={send}
          onAttach={attach}
          busy={busy}
          aiPaused={aiPaused}
        />
        <p className="mt-2 text-center text-[11px] text-neutral-400">
          Book answers come from your own data; product help comes from the docs. Double-check
          figures before acting on them.
        </p>
      </div>
    </div>
  );
}
